import { defineStore } from "pinia";
import { computed, ref } from "vue";

import type { Tutorial } from "../tutorials.data";

/**
 * Which tutorial the persistent player is showing, if any.
 *
 * A store rather than local state in the Tutorials page: the player is mounted at the app shell
 * (BaseView) so it outlives navigation, while the page that starts a video is a route that gets
 * unmounted the moment the user goes somewhere else.
 */
export const useTutorialPlayerStore = defineStore("tutorialPlayer", () => {
  // state
  const activeTutorial = ref<Tutorial | null>(null);
  const isHidden = ref(false);

  // setters
  const openTutorial = (tutorial: Tutorial) => {
    activeTutorial.value = tutorial;
    isHidden.value = false;
  };

  // Closing destroys the iframe, which also ends any picture-in-picture the user started from
  // Trupeer's player — the floating video is bound to the `<video>` element inside it. This is the
  // only action that tears the player down; hiding deliberately does not.
  const closeTutorial = () => {
    activeTutorial.value = null;
    isHidden.value = false;
  };

  // Hiding is what a user reaches for once the video is running in Trupeer's picture-in-picture and
  // the in-page player has nothing left to show. It cannot happen automatically: no event crosses
  // the cross-origin iframe boundary to tell us picture-in-picture started or ended.
  const hidePlayer = () => {
    isHidden.value = true;
  };

  const showPlayer = () => {
    isHidden.value = false;
  };

  const isPlaying = computed(() => activeTutorial.value !== null);

  return {
    // state
    activeTutorial,
    isHidden,

    // setters
    openTutorial,
    closeTutorial,
    hidePlayer,
    showPlayer,

    // computed
    isPlaying,
  };
});
